import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, DataListItem } from '@node-space/ui-components'
import { RequestErrorCallout } from 'components/Errors/RequestErrorCallout'
import { useCustomerMutation } from 'hooks/mutations/Customers/useCustomerMutation'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { CustomerDetailsWrapper } from 'pages/Customers/ConfirmCustomer/CustomerDetails'
import { CUSTOMER_API_URL } from 'services/CustomerService'
import { CustomerEntityType } from 'types/customers'
import { ConfirmCustomerData, EmbeddedCustomerPayload } from '../helpers/types'
import {
  getBusinessCustomerPayload,
  getCustomerTypeLabel,
  getIndividualCustomerPayload,
} from '../helpers/utils'
import { AddCustomerActions } from './AddCustomerActions'
import { ConfirmBusiness } from './ConfirmBusiness'
import { ConfirmIndividual } from './ConfirmIndividual'

export interface AddCustomerConfirmDetailsProps {
  customerData: ConfirmCustomerData
  onSuccess: () => void
  onCancel: () => void
}

export const AddCustomerConfirmDetails = ({
  customerData,
  onSuccess,
  onCancel,
}: AddCustomerConfirmDetailsProps) => {
  const { t } = useTranslation()
  const { requestError, handleRequestError, resetRequestError } = useMappedErrorMessage()
  const { mutate: submitEmbeddedCustomer, isPending: isSubmittingCustomer } =
    useCustomerMutation<EmbeddedCustomerPayload>()

  const { business, individual } = customerData || {}

  const customerType = useMemo(
    () => (business ? CustomerEntityType.COMPANY : CustomerEntityType.INDIVIDUAL),
    [business]
  )

  const isBusinessCustomer = useMemo(
    () => customerType === CustomerEntityType.COMPANY,
    [customerType]
  )

  const onConfirmDetails = () => {
    resetRequestError()
    const payload: EmbeddedCustomerPayload = {
      type: customerType,
      riskScore: (isBusinessCustomer ? business : individual)?.data?.riskScore,
      useCase: (isBusinessCustomer ? business : individual)?.data?.useCase,
      externalReference:
        (isBusinessCustomer ? business : individual)?.data?.externalReference || null,
      ...(isBusinessCustomer
        ? {
            company: getBusinessCustomerPayload(business?.data),
          }
        : {
            individual: getIndividualCustomerPayload(individual?.data),
          }),
    }
    submitEmbeddedCustomer(payload, {
      onSuccess,
      onError: error => handleRequestError(error, CUSTOMER_API_URL, 'submitEmbeddedCustomer', true),
    })
  }

  return (
    <>
      <DataListItem
        id="customerType"
        title={t('type')}
        body={getCustomerTypeLabel(customerType, t)}
      />
      <CustomerDetailsWrapper>
        {isBusinessCustomer ? (
          <ConfirmBusiness business={business} />
        ) : (
          <ConfirmIndividual individual={individual} />
        )}
      </CustomerDetailsWrapper>
      {requestError?.show && (
        <Box paddingT={24}>
          <RequestErrorCallout requestError={requestError} />
        </Box>
      )}
      <AddCustomerActions
        onCancel={onCancel}
        onConfirmDetails={onConfirmDetails}
        isLoadingConfirm={isSubmittingCustomer}
      />
    </>
  )
}
